import {
  ArrowUpRight,
  ChevronDown,
  Globe,
  ShieldCheck,
  Smartphone,
} from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { openPrivacyChoices } from './CookieBanner';

type FooterLink = {
  label: string;
  href: string;
  external?: boolean;
};

type FooterSection = {
  title: string;
  links: readonly FooterLink[];
};

const footerSections: readonly FooterSection[] = [
  {
    title: 'Marketplace',
    links: [
      { label: 'Human Made', href: '/human-made' },
      { label: 'Digital Products', href: '/digital-products' },
      { label: 'Services', href: '/services' },
      { label: 'Courses', href: '/courses' },
      { label: 'Communities', href: '/communities' },
      { label: 'Vacancies & Jobs', href: '/jobs' },
      { label: 'Explore Catalog', href: '/search' },
    ],
  },
  {
    title: 'Sell & Monetize',
    links: [
      { label: 'Start Selling', href: '/sell' },
      { label: 'Fees and Plans', href: '/fees-plans' },
      { label: 'Payout Information', href: '/payout-info' },
      { label: 'Selling Tips', href: '/selling-tips' },
      { label: 'Move from Gumroad', href: '/move-from-gumroad' },
      { label: 'Move from Whop', href: '/move-from-whop' },
    ],
  },
  {
    title: 'Apps & Platform',
    links: [
      { label: 'Wersee for Android', href: '/download' },
      { label: 'Mobile Web App', href: '/mobile' },
      { label: 'Point of Sale (POS)', href: '/pos' },
      { label: 'AI Companion (Buddy)', href: '/buddy' },
      { label: 'System Status', href: '/status' },
      { label: 'Safety & Verification', href: '/verify' },
    ],
  },
  {
    title: 'Support & Safety',
    links: [
      { label: 'Help Center', href: '/support' },
      { label: 'Buyer Protection', href: '/buyer-protection' },
      { label: 'Live Chat', href: '/live-chat' },
      {
        label: 'Billing Portal',
        href: 'https://billing.wersee.com',
        external: true,
      },
      { label: 'Warranty & Refund Rules', href: '/warranty-rules' },
      { label: 'Community Guidelines', href: '/guidelines' },
    ],
  },
  {
    title: 'Company & Legal',
    links: [
      { label: 'About Wersee', href: '/about' },
      { label: 'Journal & Stories', href: '/blog' },
      { label: 'Transparency', href: '/transparency' },
      { label: 'Terms of Service', href: '/terms' },
      { label: 'Privacy Policy', href: '/privacy-policy' },
      { label: 'Cookie Settings', href: '/cookies' },
      { label: 'Imprint & Legal', href: '/imprint' },
    ],
  },
];

export const Footer = () => {
  const [openSections, setOpenSections] = useState<Record<string, boolean>>({});

  const toggleSection = (title: string) => {
    setOpenSections((prev) => ({
      ...prev,
      [title]: !prev[title],
    }));
  };

  return (
    <footer className="border-t border-black/[0.08] bg-[#f5f5f7] text-neutral-800 transition-colors duration-200 dark:border-white/[0.08] dark:bg-[#09090b] dark:text-neutral-300">
      <div className="mx-auto max-w-[1280px] px-4 py-10 sm:px-6 lg:px-8">
        {/* Apple-style disclaimer & trust bar */}
        <div className="border-b border-black/[0.08] pb-7 dark:border-white/[0.08]">
          <div className="grid gap-5 lg:grid-cols-[1.5fr_1fr] lg:items-center">
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <img
                  src="/brand/wersee-icon-192.png"
                  alt=""
                  className="h-5 w-5 rounded-md object-cover"
                />
                <span className="text-[13px] font-semibold tracking-tight text-neutral-950 dark:text-white">
                  Wersee Ecosystem
                </span>
              </div>
              <p className="text-[11px] leading-relaxed text-neutral-500 dark:text-neutral-400">
                Wersee is an independent commerce and creator platform for digital products, freelance services, online courses, and memberships. All orders and payments are protected by end-to-end encryption and buyer protection.
              </p>
            </div>

            {/* Quick status & download pills */}
            <div className="flex flex-wrap items-center gap-2 lg:justify-end">
              <Link
                to="/buyer-protection"
                className="inline-flex items-center gap-1.5 rounded-full border border-black/[0.06] bg-white/70 px-3 py-1.5 text-[11px] font-medium text-neutral-700 shadow-2xs backdrop-blur-xs transition hover:bg-white hover:text-neutral-950 dark:border-white/[0.08] dark:bg-white/[0.04] dark:text-neutral-300 dark:hover:bg-white/[0.08] dark:hover:text-white"
              >
                <ShieldCheck className="h-3.5 w-3.5 text-emerald-600 dark:text-emerald-400" />
                <span>Buyer Protection</span>
              </Link>

              <Link
                to="/status"
                className="inline-flex items-center gap-1.5 rounded-full border border-black/[0.06] bg-white/70 px-3 py-1.5 text-[11px] font-medium text-neutral-700 shadow-2xs backdrop-blur-xs transition hover:bg-white hover:text-neutral-950 dark:border-white/[0.08] dark:bg-white/[0.04] dark:text-neutral-300 dark:hover:bg-white/[0.08] dark:hover:text-white"
              >
                <span className="relative flex h-2 w-2">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
                  <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500" />
                </span>
                <span>All systems operational</span>
              </Link>

              <Link
                to="/download"
                className="inline-flex items-center gap-1.5 rounded-full border border-black/[0.06] bg-white/70 px-3 py-1.5 text-[11px] font-medium text-neutral-700 shadow-2xs backdrop-blur-xs transition hover:bg-white hover:text-neutral-950 dark:border-white/[0.08] dark:bg-white/[0.04] dark:text-neutral-300 dark:hover:bg-white/[0.08] dark:hover:text-white"
              >
                <Smartphone className="h-3.5 w-3.5 text-indigo-600 dark:text-indigo-400" />
                <span>Android App</span>
              </Link>
            </div>
          </div>
        </div>

        {/* Directory columns (Apple 5-column layout with mobile accordions) */}
        <div className="py-8">
          <div className="grid gap-1 sm:grid-cols-2 sm:gap-8 md:grid-cols-3 lg:grid-cols-5">
            {footerSections.map((section) => {
              const isOpen = Boolean(openSections[section.title]);
              return (
                <div
                  key={section.title}
                  className="border-b border-black/[0.06] py-3 sm:border-b-0 sm:py-0 dark:border-white/[0.06]"
                >
                  {/* Mobile header button */}
                  <button
                    type="button"
                    onClick={() => toggleSection(section.title)}
                    className="flex w-full items-center justify-between text-left sm:hidden"
                    aria-expanded={isOpen}
                  >
                    <span className="text-[12px] font-semibold tracking-tight text-neutral-950 dark:text-white">
                      {section.title}
                    </span>
                    <ChevronDown
                      className={`h-4 w-4 text-neutral-400 transition-transform duration-200 dark:text-neutral-500 ${
                        isOpen ? 'rotate-180' : ''
                      }`}
                    />
                  </button>

                  {/* Desktop static heading */}
                  <h3 className="hidden text-[12px] font-semibold tracking-tight text-neutral-950 sm:block dark:text-white">
                    {section.title}
                  </h3>

                  {/* Links list */}
                  <ul
                    className={`mt-2.5 space-y-2 sm:block ${
                      isOpen ? 'block pt-1' : 'hidden'
                    }`}
                  >
                    {section.links.map((link) => (
                      <li key={link.href}>
                        {'external' in link && link.external ? (
                          <a
                            href={link.href}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-[12px] text-neutral-500 transition-colors hover:text-neutral-950 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 dark:text-neutral-400 dark:hover:text-white"
                          >
                            <span>{link.label}</span>
                            <ArrowUpRight className="h-3 w-3 opacity-60" />
                          </a>
                        ) : (
                          <Link
                            to={link.href}
                            className="inline-flex text-[12px] text-neutral-500 transition-colors hover:text-neutral-950 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 dark:text-neutral-400 dark:hover:text-white"
                          >
                            {link.label}
                          </Link>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </div>

        {/* Apple-style bottom section */}
        <div className="border-t border-black/[0.08] pt-5 text-[11px] text-neutral-500 dark:border-white/[0.08] dark:text-neutral-400">
          <p className="mb-4 text-[11px] text-neutral-500 dark:text-neutral-400">
            More ways to explore: Download the{' '}
            <Link to="/download" className="text-indigo-600 underline dark:text-indigo-400">
              Wersee Android App
            </Link>
            , explore{' '}
            <Link to="/pos" className="text-indigo-600 underline dark:text-indigo-400">
              Wersee Point of Sale
            </Link>
            , or visit our{' '}
            <Link to="/support" className="text-indigo-600 underline dark:text-indigo-400">
              Help Center
            </Link>
            .
          </p>

          <div className="flex flex-col gap-3 pt-2 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
              <span>© {new Date().getFullYear()} Wersee Inc. All rights reserved.</span>
              <span className="hidden text-neutral-300 dark:text-neutral-700 sm:inline">|</span>
              <Link
                to="/privacy-policy"
                className="hover:text-neutral-950 hover:underline dark:hover:text-white"
              >
                Privacy Policy
              </Link>
              <span className="text-neutral-300 dark:text-neutral-700">·</span>
              <Link
                to="/terms"
                className="hover:text-neutral-950 hover:underline dark:hover:text-white"
              >
                Terms of Use
              </Link>
              <span className="text-neutral-300 dark:text-neutral-700">·</span>
              <Link
                to="/buyer-protection"
                className="hover:text-neutral-950 hover:underline dark:hover:text-white"
              >
                Buyer Protection
              </Link>
              <span className="text-neutral-300 dark:text-neutral-700">·</span>
              <Link
                to="/imprint"
                className="hover:text-neutral-950 hover:underline dark:hover:text-white"
              >
                Imprint & Legal
              </Link>
              <span className="text-neutral-300 dark:text-neutral-700">·</span>
              <Link
                to="/cookies"
                className="hover:text-neutral-950 hover:underline dark:hover:text-white"
              >
                Cookies
              </Link>
              <span className="text-neutral-300 dark:text-neutral-700">·</span>
              <button
                type="button"
                onClick={openPrivacyChoices}
                className="text-left hover:text-neutral-950 hover:underline dark:hover:text-white"
              >
                Privacy Choices
              </button>
            </div>

            <div className="flex items-center gap-1.5 text-neutral-600 dark:text-neutral-400">
              <Globe className="h-3.5 w-3.5" />
              <span>Netherlands / Europe (EUR €)</span>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};
